<template>
    <div class="text-center">
        <h3 class="title">{{ $t("institution.institutionUsers") }}</h3>
    </div>
    <div class="text-end" v-if="institutionSettings">
        <span class="text-end">{{ $t("institution.users.actualNumberOfUsers") }}: </span>
        <span class="fw-bold ">{{ users.length }}</span>
        /
        <span class="fw-bold">{{ institutionSettings.paid_users }}</span>
    </div>
    <div class="table-responsive">
        <table class="table table-responsive table-hover w-100 mt-3 mb-0">
            <thead class="table_head">
                <tr>
                    <th class="">{{ $t("institution.users.name") }}</th>
                    <th class="">{{ $t("institution.users.email") }}</th>
                    <th class="">
                        {{ $t("institution.users.lastLogin") }} / {{ $t("institution.users.dateOfInvitation") }}
                    </th>
                    <th class="">{{ $t("institution.users.isAdmin") }}</th>
                    <th class="">{{ $t("institution.users.numberOfCompanies") }}</th>
                    <th class="">{{ $t("institution.users.active") }}</th>
                    <th class=""></th>
                </tr>
            </thead>
            <tbody>
                <!-- Users that are in the institution -->
                <tr v-for="(user, i) in users" :key="'users' + i" class="align-middle">
                    <td v-if="user.name && !isEditing(user.id)" class="">
                        <span>{{ user.name }}</span>
                    </td>
                    <td v-else v-if="!isEditing(user.id)" class="">
                        <span>{{ user.username }}</span>
                    </td>
                    <td v-if="isEditing(user.id)" class="">
                        <input type="text" v-model="user.name" class="form-control">
                    </td>
                    <td class="">
                        <span>{{ user.email }}</span>
                    </td>
                    <td class="">
                        <i18n-d tag="span" :value="new Date(user.last_login)"
                            :format="{ day: 'numeric', month: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' }">
                        </i18n-d>
                    </td>
                    <td v-if="!isEditing(user.id)" class="">
                        <span v-if="user.admin">
                            <icon icon="True" class="icon_true" />
                        </span>
                        <span v-else>
                            <icon icon="False" class="icon_false" />
                        </span>
                    </td>
                    <td class="">
                        <span>{{ user.user_in_contact_with_count }}</span>
                    </td>
                    <td class="">
                        <icon icon="True" class="icon_true" />
                    </td>
                    <td v-if="isEditing(user.id)" class="">
                        <button @click="toggleAdmin(user)" class="btn btn-link p-0">
                            <icon v-if="user.admin" icon="True" class="icon_true"></icon>
                            <icon v-else icon="False" class="icon_false"></icon>
                        </button>
                    </td>
                    <td class="">
                        <div v-if="user.id !== $store.getters.user.id" class="d-flex">
                            <div v-if="!isEditing(user.id)" class="d-flex">
                                <button @click="deleteUser(user.id)" class="btn">
                                    <icon icon="Bin" class="icon_bin" />
                                </button>
                                <button @click="editUser(user.id)" class="btn">
                                    <icon icon="Edit" class="icon_edit" />
                                </button>
                            </div>
                            <div v-if="isEditing(user.id)" class="d-flex">
                                <button @click="saveUser(user)" class="btn me-2 btn_main">
                                    <span>{{ $t("utils.save") }}</span>
                                </button>
                                <button @click="cancelEditUser(user)" class="btn btn_main">
                                    <span>{{ $t("utils.cancel") }}</span>
                                </button>
                            </div>
                        </div>
                    </td>
                </tr>

                <!-- Users that are invited to the institution -->
                <tr v-for="(user, i) in usersInvited" :key="'invited' + i" class="align-middle bg-dark"
                    :class="{ 'border-top': i === 0 }">
                    <td>
                        <span>Uživatel pozván</span>
                    </td>
                    <td>
                        <span>{{ user.email }}</span>
                    </td>
                    <td>
                        <i18n-d tag="span" :value="new Date(user.created)"
                            :format="{ day: 'numeric', month: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }">
                        </i18n-d>
                    </td>
                    <td>
                        <span v-if="user.admin">
                            <icon icon="True" class="icon_true" />
                        </span>
                        <span v-else>
                            <icon icon="False" class="icon_false" />
                        </span>
                    </td>
                    <td>
                        <span>0</span>
                    </td>
                    <td>
                        <icon icon="False" class="icon_false" />
                    </td>
                    <td>
                        <div class="d-flex">
                            <button @click="deleteInvitedUser(user.id)" class="btn">
                                <icon icon="Bin" class="icon_bin" />
                            </button>
                        </div>
                    </td>

                </tr>

                <!-- Modal for adding users to institution -->
                <tr>
                    <td colspan="7" class="w-100" style="background-color: white;">
                        <div class="py-2 text-center w-100">
                            <InstitutionAddUserModal :fromAdmin="fromAdmin" :institutionId="institutionId" />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import InstitutionAddUserModal from '@/components/Institution/InstitutionAddUserModal.vue';

export default {
    components: {
        InstitutionAddUserModal,
    },
    props: {
        institutionId: {
            type: Number,
            required: true
        },
        fromAdmin: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            users: [],
            usersInvited: [],
            institutionSettings: {},
            userEditingId: null,
            originalUserData: {},
        }
    },
    created() {
        this.loadData();
    },
    methods: {
        loadData: function () {
            this.$store.getters.api.get(`/institution/${this.institutionId}/settings`).then(function (response) {
                this.institutionSettings = response.data.institution;
            }.bind(this));
            this.$store.getters.api.get(`/institution/${this.institutionId}/settings/users`).then(function (response) {
                this.users = response.data.items;
            }.bind(this));
            this.$store.getters.api.get(`/institution/${this.institutionId}/settings/users/invited`).then(function (response) {
                this.usersInvited = response.data.items;
            }.bind(this));
        },
        deleteUser: function (iuId) {
            this.$store.getters.api.delete(`/institution/${this.institutionId}/user/delete`, { data: { userId: iuId } })
                .then(response => {
                    this.loadData()
                })
        },

        deleteInvitedUser: function (userId) {
            this.$store.getters.api.delete(`/institution/${this.institutionId}/user/invited/delete`, { data: { id: userId } })
                .then(response => {
                    this.loadData()
                })
        },
        isEditing(iuId) {
            return this.userEditingId === iuId; // Check if the current user is being edited
        },
        toggleAdmin(user) {
            user.admin = !user.admin
        },
        cancelEditUser: function (iu) {
            this.userEditingId = null;
            this.loadData()
        },
        editUser: function (iuId) {
            if (this.userEditingId === iuId) {
                this.userEditingId = null;
            } else {
                this.userEditingId = iuId;
                // Store the original data for potential canceling
                this.originalUserData = { ...this.users.find(tag => tag.id === iuId) };
            }
        },
        saveUser: function (iu) {
            const updatedUserData = {
                id: iu.id,
                admin: iu.admin,
                name: iu.name
            };
            this.$store.getters.api.put(`/institution/${this.institutionId}/user/edit`, updatedUserData).then(response => {
                this.loadData();
                this.userEditingId = null;
            })
        }
    },
}
</script>

<style scoped lang="scss">
@import "@/scss/abstracts/variables";
@import "@/scss/base/typography.scss";

.title {
    font-size: clamp(24px, 3.2vw, 40px);
    color: $text;
    font-weight: 500;
    font-family: $font-heading;
}

svg {
    width: 35px;
    height: 35px;
}

.icon_true {
    color: green;
}

.icon_false {
    color: $red;
}

.icon_bin,
.icon_edit {
    stroke: $purple;
    color: $purple;
    background-color: transparent;
}

// Users invited table
.table>tbody>tr.bg-dark>td,
.table>tbody>tr.bg-dark>th {
    opacity: 0.6;
    background-color: #f8f9fa;
}

.border-top {
    border-top: 3px solid $dark-purple !important;
}
</style>